import React from 'react'
import Typography from '@mui/material/Typography'
import styled from 'styled-components'
import Box from '@mui/material/Box'
import HpLogo from '@/assets/images/hp-logo.png'
import HpLogoInstitue from '@/assets/images/hp-image-institute.png'
import ElixirLogo from '@/assets/images/elixir_czechrepublic_white_background.png'
import EatrisLogo from '@/assets/images/eatris.png'
import Location from '@/assets/icon/Location'
import Email from '@/assets/icon/Email'
import Phone from '@/assets/icon/Phone'
import Link from '@mui/material/Link'
import DocumentTitle from '@/components/common/DocumentTitle'

const InfoBanner = styled(Box)`
  background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
  border: 2px solid #ffc107;
  border-radius: 8px;
  padding: 12px 40px;
  margin: 40px auto 20px auto;
  max-width: 1200px;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.15);
`

const BannerTitle = styled(Typography)`
  font-weight: 600;
  margin-bottom: 6px;
  color: #856404;
  font-size: 1rem;
`

const ExamplesBox = styled(Box)`
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border: 2px solid #9ca3af;
  border-radius: 8px;
  padding: 24px 32px;
  margin: 40px auto;
  max-width: 900px;
  box-shadow: 0 2px 8px rgba(156, 163, 175, 0.15);
`

const ExamplesTitle = styled(Typography)`
  font-weight: 600;
  margin-bottom: 16px;
  color: #374151;
  font-size: 1.25rem;
`

const ExampleLink = styled.div`
  margin: 12px 0;
  text-align: left;
  
  a {
    color: #0066cc;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.95rem;
    
    &:hover {
      text-decoration: underline;
      color: #004499;
    }
  }
  
  span {
    color: #6b7280;
    font-size: 0.9em;
    margin-left: 8px;
  }
`

const HomePageHeading = styled(Typography)`
  margin-top: 0;
  margin-bottom: 40px;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 3rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`

const ContentSection = styled(Box)`
  margin: 40px auto;
  max-width: 900px;
`

const SectionHeading = styled(Typography)`
  font-weight: 700;
  margin-bottom: 20px;
  margin-top: 40px;
  color: #1976d2;
  border-bottom: 3px solid #1976d2;
  padding-bottom: 8px;
`

const StatsContainer = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin: 40px 0;
  max-width: 900px;
`

const StatTile = styled(Box)`
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border: 2px solid #1976d2;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(25, 118, 210, 0.25);
  }
`

const StatNumber = styled(Typography)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #0d47a1;
  margin-bottom: 8px;
`

const StatLabel = styled(Typography)`
  font-size: 0.95rem;
  color: #1565c0;
  line-height: 1.4;
`

const HomePageContactWrapper = styled.div`
  max-width: 355px;

`

const HomePageLink = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 25px;

  p {
    margin-left: 25px;
  }
`

const HomePageLinkIconWrapper = styled.div`
  display: inline-block;
  width: 23px;
  color: ${props => props.theme.palette.primary.main};
`

const HomeImagesWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  margin-bottom: 25px;
  margin-top: 25px;
`

const DataUsageDisclaimer = styled(Box)`
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border: 2px solid #1976d2;
  border-radius: 8px;
  padding: 30px 40px;
  margin: 50px auto;
  max-width: 900px;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.15);
`

const DisclaimerTitle = styled(Typography)`
  font-weight: 700;
  margin-bottom: 20px;
  color: #0d47a1;
  text-align: center;
`

const DisclaimerText = styled(Typography)`
  margin-bottom: 16px;
  line-height: 1.8;
  color: #1565c0;
  
  strong {
    font-weight: 600;
    color: #0d47a1;
  }
`

const ContactBox = styled(Box)`
  background: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  padding: 16px 20px;
  margin-top: 20px;
  text-align: center;
  
  a {
    color: #1976d2;
    font-weight: 600;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`

export default function HomePage() {
    return (
        <div>
            <DocumentTitle title={'Home'} />
            <InfoBanner>
                <BannerTitle variant='h6' align='center'>
                    🎉 CzechGenome Database Update - November 2025
                </BannerTitle>
                <Typography variant='body2' align='center' style={{ lineHeight: 1.6, color: '#333', fontSize: '0.9rem' }}>
                    CzechGenome was upgraded and recalculated in November 2025. The main dataset now consists of approximately 1,500 healthy donors, and the lung cancer screening individuals dataset is now available. Current activities focus on secondary analysis of existing genomic data. We expect the main dataset will be updated with new results from approximately 3,000 total WGS samples.
                </Typography>
            </InfoBanner>
            <div>
                <HomePageHeading variant='h1' align='center'>
                    CzechGenome
                </HomePageHeading>
            </div>

            <Box sx={{ maxWidth: '900px', margin: '0 auto 30px auto', textAlign: 'center' }}>
                <Typography variant='h4' style={{ fontWeight: 600, marginBottom: '16px', color: '#1976d2' }}>
                    What is CzechGenome?
                </Typography>
                <Typography variant='body1' style={{ fontSize: '1.1rem', lineHeight: 1.8, color: '#333' }}>
                    CzechGenome is an open reference database of genetic variants of the Czech population for more precise interpretation of genetic findings.
                </Typography>
            </Box>

            <ExamplesBox>
                <ExamplesTitle variant='h5' align='center'>
                    🔍 Try These Examples
                </ExamplesTitle>
                <ExampleLink>
                    <Link href='https://czechgenome.cz/gene/BRCA2?datasetId=17652777241484514962' target='_blank'>
                        Gene: BRCA2
                    </Link>
                    <span>- breast cancer susceptibility gene</span>
                </ExampleLink>
                <ExampleLink>
                    <Link href='https://czechgenome.cz/variant/13-32312076-G-A?back=%2Fgene%2FBRCA2&datasetId=17652777241484514962' target='_blank'>
                        Variant: 13-32312076-G-A
                    </Link>
                    <span>- specific variant in BRCA2 gene</span>
                </ExampleLink>
                <ExampleLink>
                    <Link href='https://czechgenome.cz/region/chr13-32310516-32320516?datasetId=17652777241484514962' target='_blank'>
                        Region: chr13:32310516-32320516
                    </Link>
                    <span>- 10kb region containing BRCA2</span>
                </ExampleLink>
            </ExamplesBox>

            <ContentSection>
                <SectionHeading variant='h2'>
                    About CzechGenome
                </SectionHeading>
                <Typography variant='body1' paragraph style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                    The genome of the Czech – and more generally Slavic – population has been underrepresented in international databases until now. This has complicated the interpretation of certain genetic variants and thus the assessment of the actual risk of tumor diseases in Czech patients. The CzechGenome project addresses this gap by creating the first comprehensive reference database of the Czech genome.
                </Typography>

                <SectionHeading variant='h2'>
                    What Does This Mean for the Public?
                </SectionHeading>
                <Typography variant='body1' paragraph style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                    CzechGenome enables:
                </Typography>
                <Box component='ul' sx={{ paddingLeft: '40px', marginBottom: '30px', '& li': { marginBottom: '16px' } }}>
                    <li>
                        <Typography variant='body1' style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                            <strong>Identification of individuals with increased hereditary risk</strong> of certain diseases, particularly cancer
                        </Typography>
                    </li>
                    <li>
                        <Typography variant='body1' style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                            <strong>Targeted prevention and monitoring</strong> programs tailored to individual genetic profiles
                        </Typography>
                    </li>
                    <li>
                        <Typography variant='body1' style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                            <strong>Results communicated after professional verification and through genetic counseling</strong> within a clear ethical framework
                        </Typography>
                    </li>
                </Box>

                <Box sx={{ marginTop: '50px', marginBottom: '40px' }}>
                    <Typography variant='h4' align='center' style={{ fontWeight: 600, marginBottom: '30px', color: '#1976d2' }}>
                        CzechGenome in Numbers
                    </Typography>
                    <StatsContainer>
                        <StatTile>
                            <StatNumber variant='h2'>
                                3,000+
                            </StatNumber>
                            <StatLabel variant='body1'>
                                WGS genomes (research)
                            </StatLabel>
                        </StatTile>
                        <StatTile>
                            <StatNumber variant='h2'>
                                2,000+
                            </StatNumber>
                            <StatLabel variant='body1'>
                                in CzechGenome reference database
                            </StatLabel>
                        </StatTile>
                        <StatTile>
                            <StatNumber variant='h2'>
                                997
                            </StatNumber>
                            <StatLabel variant='body1'>
                                genomes analyzed for secondary findings (ACMG)
                            </StatLabel>
                        </StatTile>
                    </StatsContainer>
                </Box>

                <SectionHeading variant='h2'>
                    Key Findings
                </SectionHeading>
                <Typography variant='body1' paragraph style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                    More than 3,000 genomes of healthy Czech volunteers have been analyzed using whole genome sequencing in IMTM laboratories. From over 2,000 completely sequenced genomes, a freely accessible reference database of Czech genome variants has been created at{' '}
                    <Link href='https://www.czechgenome.cz' target='_blank'>
                        www.czechgenome.cz
                    </Link>.
                </Typography>
                <Typography variant='body1' paragraph style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                    Preliminary analysis shows that in a few percent of otherwise healthy individuals, clearly pathogenic or likely pathogenic variants can be found that are associated with increased risk of selected tumor, cardiovascular, metabolic, and other diseases and can be clinically addressed through targeted prevention.
                </Typography>
                <Typography variant='body1' paragraph style={{ fontSize: '1rem', lineHeight: 1.8, fontWeight: 600 }}>
                    Examples of the most frequently affected genes include:
                </Typography>
                <Box component='ul' sx={{ paddingLeft: '40px', marginBottom: '30px', '& li': { marginBottom: '12px' } }}>
                    <li>
                        <Typography variant='body1' style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                            <strong>BRCA1 (0.3%) and BRCA2 (0.6%)</strong> – genes responsible for DNA repair and maintaining genetic stability in cells, thereby suppressing tumor growth
                        </Typography>
                    </li>
                    <li>
                        <Typography variant='body1' style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                            <strong>LDLR (0.3%)</strong> – serves as a receptor on cell surfaces, especially in the liver, that captures LDL cholesterol particles from the blood
                        </Typography>
                    </li>
                </Box>

                <SectionHeading variant='h2'>
                    About the Project
                </SectionHeading>
                <Typography variant='body1' paragraph style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                    The CzechGenome project was initiated by a group of scientists from the{' '}
                    <Link href='https://www.imtm.cz/' target='_blank'>
                        Institute of Molecular and Translational Medicine (IMTM)
                    </Link>
                    , Faculty of Medicine and Dentistry,{' '}
                    <Link href='https://www.upol.cz/' target='_blank'>
                        Palacký University Olomouc
                    </Link>
                    , led by its director Dr. Marián Hajdúch, who also serves as Medical Director of the National Cancer Research Institute (NÚVR).
                </Typography>

                <Typography variant='h6' style={{ fontWeight: 600, marginTop: '30px', marginBottom: '16px', color: '#1976d2' }}>
                    Funding and Collaborations
                </Typography>
                <Typography variant='body1' paragraph style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                    Whole genome sequencing (WGS) and data processing is costly, which is why CzechGenome is funded through a series of successive projects and initiatives. The project builds upon the first thousand genomes from the{' '}
                    <Link href='https://www.mpo.cz/' target='_blank'>
                        ENIGMA project (Ministry of Industry and Trade)
                    </Link>
                    {' '}carried out in collaboration with the{' '}
                    <Link href='https://www.iabio.eu/' target='_blank'>
                        Institute of Applied Biotechnologies, a.s.
                    </Link>
                </Typography>
                <Typography variant='body1' paragraph style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                    The project has since expanded through national initiatives including{' '}
                    <Link href='https://www.acgt.cz/o-projektu-a-c-g-t-1/' target='_blank'>
                        ACGT I
                    </Link>
                    {' '}and{' '}
                    <Link href='https://www.acgt.cz/o-projektu-a-c-g-t-2/' target='_blank'>
                        ACGT II
                    </Link>
                    {' '}(led by{' '}
                    <Link href='https://www.muni.cz/' target='_blank'>
                        Masaryk University
                    </Link>
                    {' '}and funded by the Ministry of Education, Youth and Sports), as well as the{' '}
                    <Link href='https://www.imtm.cz/research-projects/permed-t2ba' target='_blank'>
                        PerMed
                    </Link>
                    {' '}project (Technology Agency of the Czech Republic),{' '}
                    <Link href='https://www.fnol.cz/projekt-enoch' target='_blank'>
                        ENOCH
                    </Link>
                    , National Cancer Research Institute (NÚVR), and{' '}
                    <Link href='https://imtm.cz/research-projects/salvage?language_content_entity=en' target='_blank'>
                        SALVAGE
                    </Link>.
                </Typography>

                <Typography variant='h6' style={{ fontWeight: 600, marginTop: '30px', marginBottom: '16px', color: '#1976d2' }}>
                    Research Infrastructure
                </Typography>
                <Typography variant='body1' paragraph style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                    Large research infrastructures play a key role in the project, particularly{' '}
                    <Link href='https://www.eatris.cz/' target='_blank'>
                        EATRIS-CZ
                    </Link>
                    {' '}(sequencing, clinical trial management, tools, and database aggregation) and{' '}
                    <Link href='https://www.elixir-czech.cz/' target='_blank'>
                        ELIXIR-CZ
                    </Link>
                    {' '}(infrastructure for large-scale data storage and management).
                </Typography>

                <Box sx={{
                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                    padding: '20px 30px',
                    borderRadius: '8px',
                    borderLeft: '4px solid #4caf50',
                    margin: '30px 0',
                    fontStyle: 'italic'
                }}>
                    <Typography variant='body1' style={{ fontSize: '1rem', lineHeight: 1.8, color: '#2e7d32' }}>
                        &ldquo;CzechGenome gives us a national context for variant interpretation – without it, some findings are unnecessarily uncertain. Thanks to our own reference database, we can more accurately distinguish common, harmless variants from those that truly increase disease risk.&rdquo;
                    </Typography>
                    <Typography variant='body2' style={{ marginTop: '12px', fontWeight: 600, color: '#1b5e20', textAlign: 'right' }}>
                        — Dr. Marián Hajdúch, Medical Director of NÚVR
                    </Typography>
                </Box>
                <Typography variant='body1' paragraph style={{ fontSize: '1rem', lineHeight: 1.8 }}>
                    Researchers behind the project are open to collaborative research proposals and welcome valuable suggestions and comments from the research community. For further correspondence please use email contact:{' '}
                    <Link href='mailto:info@imtm.cz'>info@imtm.cz</Link>.
                </Typography>

                <DataUsageDisclaimer>
                    <DisclaimerTitle variant='h4'>
                        ⚖️ Data Usage & Licensing
                    </DisclaimerTitle>
                    <DisclaimerText variant='body1'>
                        <strong>Data Ownership:</strong> All genomic data and related information provided through CzechGenome database are the property of the{' '}
                        <Link href='https://imtm.cz/' target='_blank'>
                            Institute of Molecular and Translational Medicine (IMTM)
                        </Link>, Palacky University Olomouc.
                    </DisclaimerText>
                    <DisclaimerText variant='body1'>
                        <strong>Academic & Research Use:</strong> The data may be used for academic and research purposes only. This includes browsing, querying, and analyzing the data for non-commercial scientific research and educational activities.
                    </DisclaimerText>
                    <DisclaimerText variant='body1'>
                        <strong>Commercial Use:</strong> Any commercial use, including but not limited to downloading, distribution, or incorporation of this data into commercial products or services, requires a specific license agreement from IMTM.
                    </DisclaimerText>
                    <DisclaimerText variant='body1'>
                        <strong>License Inquiries:</strong> For information regarding commercial licensing, data access agreements, or collaborative research opportunities, please contact IMTM directly.
                    </DisclaimerText>
                    <ContactBox>
                        <Typography variant='body1' style={{ marginBottom: 8, color: '#0d47a1', fontWeight: 600 }}>
                            Contact IMTM for Licensing:
                        </Typography>
                        <Typography variant='body2'>
                            <Link href='https://imtm.cz/' target='_blank'>
                                www.imtm.cz
                            </Link>
                            {' | '}
                            <Link href='mailto:info@imtm.cz'>
                                info@imtm.cz
                            </Link>
                        </Typography>
                    </ContactBox>
                </DataUsageDisclaimer>

                <Box sx={{ marginTop: '60px', marginBottom: '40px' }}>
                    <Typography variant='h5' align='center' style={{ marginBottom: '30px', fontWeight: 600, color: '#1976d2' }}>
                        Project Support & Partners
                    </Typography>

                    <HomeImagesWrapper>
                        <div>
                            <Link href='https://www.iabio.eu/' target='_blank'>
                                <img src={HpLogo} alt='Institute of Applied Biotechnologies' style={{ maxHeight: '100px', objectFit: 'contain' }} />
                            </Link>
                        </div>
                        <div>
                            <Link href='https://www.imtm.cz/' target='_blank'>
                                <img src={HpLogoInstitue} alt='Institute of Molecular and Translational Medicine' style={{ maxHeight: '100px', objectFit: 'contain' }} />
                            </Link>
                        </div>
                    </HomeImagesWrapper>

                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '40px', marginTop: '40px', flexWrap: 'wrap' }}>
                        <Link href='https://www.elixir-czech.cz/' target='_blank'>
                            <img src={ElixirLogo} alt='ELIXIR Czech Republic' style={{ maxWidth: '250px', height: 'auto' }} />
                        </Link>
                        <Link href='https://www.eatris.cz/' target='_blank'>
                            <img src={EatrisLogo} alt='EATRIS' style={{ maxWidth: '250px', height: 'auto' }} />
                        </Link>
                    </Box>
                </Box>
            </ContentSection>

            <Box display='flex' justifyContent='center' sx={{ marginTop: '50px', marginBottom: '30px' }}>
                <HomePageContactWrapper>
                    <HomePageLink>
                        <HomePageLinkIconWrapper>
                            <Location />
                        </HomePageLinkIconWrapper>
                        <Typography variant='body1'>
                            Institute of Molecular and Translational Medicine
                            <br /> Palacký University Olomouc
                            <br /> Hněvotínská 1333/5, 779 00 Olomouc
                        </Typography>
                    </HomePageLink>
                    <HomePageLink>
                        <HomePageLinkIconWrapper>
                            <Email />
                        </HomePageLinkIconWrapper>
                        <Typography variant='body1'>
                            E-mail:&nbsp;
                            <Link href='mailto:info@imtm.cz'>
                                info@imtm.cz
                            </Link>
                        </Typography>
                    </HomePageLink>
                    <HomePageLink>
                        <HomePageLinkIconWrapper>
                            <Phone />
                        </HomePageLinkIconWrapper>
                        <Typography variant='body1'>
                            Phone:&nbsp;
                            <Link href='tel:+420585632000'>
                                +420 585 632 000
                            </Link>
                        </Typography>
                    </HomePageLink>
                </HomePageContactWrapper>
            </Box>
        </div>
    )
}
